import { connect } from "react-redux";
import { compose, lifecycle, withHandlers, withState } from "recompose";
import { find, get, isEmpty } from "lodash";

import {
  fetchPickupLocationByCode,
  fetchPickupLocations,
} from "~/apis/reference";
import { ShipmentEntity } from "~/constants/forms";
import { SHIP_TO_SHOP } from "~/constants/services";
import { PickupLocationModels, PostcodeModels } from "~/models";
import { ReferenceActions, ReferenceSelectors } from "~/redux";

export default compose(
  withState("deliveryAddress", "setDeliveryAddress", null),
  withState("extraPickupLocation", "setExtraPickupLocation", null),
  withState("pickupLocationsPostcode", "setPickupLocationsPostcode", null),
  withState("isLoadingPickupLocations", "setIsLoadingPickupLocations", false),
  connect(state => ({
    pickupLocations: ReferenceSelectors.getPickupLocations(state),
  })),
  withHandlers({
    onPickupLocationChange:
      ({ reInitializeForm }) =>
      pickupPoint =>
        reInitializeForm(PickupLocationModels.getPickupDetails(pickupPoint)),
    fetchPickupLocations: ({
      dispatch,
      notifier,
      selectedService,
      extraPickupLocation,
      setExtraPickupLocation,
      reInitializeForm,
      pickupLocationsPostcode,
      setPickupLocationsPostcode,
      pickupLocations,
      setIsLoadingPickupLocations,
    }) =>
      notifier.runAsync(
        async ({
          postcode,
          pickupLocationCode,
          clearSelectedPickupLocation = true,
        }) => {
          const isShipToShopServiceSelected = SHIP_TO_SHOP.includes(
            selectedService?.networkKey
          );
          const isNewPostcode = pickupLocationsPostcode !== postcode;
          const isPostcodeValid = PostcodeModels.isGBPostCodeFormat(postcode);
          const shouldFetchPickupLocations =
            isShipToShopServiceSelected &&
            isPostcodeValid &&
            (isNewPostcode || isEmpty(pickupLocations));

          if (shouldFetchPickupLocations) {
            try {
              setIsLoadingPickupLocations(true);

              const { data } = await dispatch(
                ReferenceActions.fetchPickupLocations({
                  postcode,
                })
              );
              setPickupLocationsPostcode(postcode);

              // clear selected pickup details on fetch new pickup locations
              if (clearSelectedPickupLocation) {
                reInitializeForm({ "outboundConsignment.pickupDetails": null });
              }

              setExtraPickupLocation(null);

              const fetchExtraPickupLocation =
                pickupLocationCode &&
                !extraPickupLocation &&
                !find(data, {
                  pickupLocation: {
                    pickupLocationCode,
                  },
                });

              // fetch different postcode extra pickup location
              if (fetchExtraPickupLocation) {
                const { data } =
                  await fetchPickupLocationByCode(pickupLocationCode);

                setExtraPickupLocation({
                  addressPoint: data.addressPoint,
                  pickupLocation: data,
                });
              }
            } finally {
              setIsLoadingPickupLocations(false);
            }
          }
        }
      ),
    fetchPickupLocationsByPostcode: ({ notifier }) =>
      notifier.runAsync(
        async ({ postcode, setPickupLocations, setIsLoading }) => {
          try {
            setIsLoading(true);
            const { data } = await fetchPickupLocations({
              searchAddress: postcode,
            });
            setPickupLocations(data);
          } catch (e) {
            setPickupLocations([]);
          } finally {
            setIsLoading(false);
          }
        }
      ),
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        selectedService,
        createShipmentValues,
        fetchPickupLocations,
        shipment,
      } = this.props;

      // fetch pickup locations on ship to shop service select
      if (
        prevProps.selectedService?.networkKey !== selectedService?.networkKey
      ) {
        const createShipmentValuesPostcode = get(
          createShipmentValues,
          ShipmentEntity.OUTBOUND_CONSIGNMENT.DELIVERY_DETAILS.ADDRESS.POSTCODE
        );
        const pickupLocationCode = get(
          shipment,
          ShipmentEntity.OUTBOUND_CONSIGNMENT.PICKUP_DETAILS
            .PICKUP_LOCATION_CODE
        );
        const shipmentPostcode = get(
          shipment,
          ShipmentEntity.OUTBOUND_CONSIGNMENT.DELIVERY_DETAILS.ADDRESS.POSTCODE
        );
        const postcode = createShipmentValuesPostcode || shipmentPostcode;

        if (postcode) {
          fetchPickupLocations({
            postcode,
            pickupLocationCode,
            clearSelectedPickupLocation: false,
          });
        }
      }
    },
  })
);
